import React, {Fragment, useCallback, useEffect, useState} from 'react';
import {Col, Container, Row, Button} from 'reactstrap';
import LazyLoad from 'react-lazyload';
import ReactMarkdown from 'react-markdown';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState} from 'funnel-schedule/phase-context';
import useBalanceText from 'utils/useBalanceText';

import PageMeta from 'components/PageMeta';
import SectionCL from 'components/ComponentLibrary/SectionCL';
import Video from 'components/Video';
import ImageCardCL from 'components/ComponentLibrary/ImageCardCL';
import {ListIcon} from 'components/Elements/IconList';
import OptinModal, {OptinModalTrigger} from 'components/OptinModal';
import Modal from 'components/Modal';
import Footer from 'components/Elements/Footer';

//@ts-ignore
import {workshopSpeakerList} from 'data/pbcc-workshop.json';
//@ts-ignore
import {speakers} from 'data/module.json';
import ExitModal from 'components/ComponentLibrary/ExitModal';

export default function WorkshopIndex({
  noExitIntent = false,
  noFixedVideo = false,
  ctaOverride = null,
  videoUrlOverride = null,
  ctaNoTimer = false,
  variant = 'default',
  ...props
}) {
  const pageTitle = 'Free Workshop: Become a Plant-Based Coach';
  const pageDescription = 'Save your spot in this free, online workshop today.';
  const ogImage =
    'https://s3.us-west-2.amazonaws.com/cdn.foodrevolution.org/pbcc/og/pbcc-w-2026-open-graph-workshop-fb-tw.png';
  const shareUrl = 'https://certificate.foodrevolution.org/workshop/';

  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before'
      }
    }
  } = usePhaseState();

  const phase = defaultSchedule.phase;
  const ctaButtonCopy = 'Save My Spot';

  const [optinCopyControl, setoptinCopyControl] = useState(props.optinCopyControl || 'control');
  useEffect(() => {
    subscribeToCampaign((variantIndex) => {
      switch (variantIndex) {
        case 1:
          setoptinCopyControl('v1');
          break;
      }
    }, 1300);
  }, []);

  const landingVideo = (
    <Video
      url="https://www.youtube.com/watch?v=CEWp1uZJg8I"
      label="PBCC - Workshop - Trailer"
      thumbnail="https://cdn.foodrevolution.org/pbcc/video-thumb/pbcc-workshop-landing_v1-dynamicthumb-20250714.gif"
      noButton
    />
  );
  return (
    <div className="page page-nosocial d-flex flex-column">
      <PageMeta title={pageTitle} description={pageDescription} url={shareUrl} ogImage={ogImage} />
      <div className="page-content">
        <SectionCL
          id="pbccw-header-cta"
          color="black"
          imgUrl="https://cdn.foodrevolution.org/pbcc/images/1409236261-lg.jpg"
          angle="none"
          order="first"
        >
          <Container>
            <Row>
              <Col>
                <div className="background-light-gray rounded box-shadow-black-50 p-4 p-lg-5">
                  <div className="text-center">
                    {
                      {
                        control: <p>Free Live Special Event</p>,
                        v1: (
                          <h5 className="mt-0 text-400">
                            Learn From the Experts: <b>November 18 – 20, 2025</b>
                          </h5>
                        )
                      }[optinCopyControl]
                    }
                    <div className="d-flex flex-column align-items-center justify-content-center">
                      <p className="text-h5 text-800 text-green mb-0">Food Revolution Network’s</p>
                      <img
                        src="https://cdn.foodrevolution.org/pbcc/pbcc-logo-workshop.svg"
                        alt="plant-based coaching workshop logo"
                        width={600}
                        height={138}
                        style={{maxHeight: '100px', width: 'auto'}}
                      />
                    </div>
                    <h2 className="my-3">
                      Nutrition, Healing, and Coaching <br className="d-none d-xl-block" />
                      in the Modern World
                    </h2>
                    <h4 className="text-400 mt-0">
                      Discover the power of plant-based coaching and how it can change your life and your world.
                    </h4>
                    {optinCopyControl === 'control' && (
                      <h5 className="mt-0 mb-3 text-400">
                        Learn From the Experts: <b>November 18 – 20, 2025</b>
                      </h5>
                    )}
                    <OptinModalTrigger>{ctaButtonCopy}</OptinModalTrigger>
                    <p className="balance-text mt-3 mb-0">
                      <b>How It Works:</b> Each day of the workshop, we’ll bring you a new live session{' '}
                      <br className="d-none d-xl-block" />
                      starting at 12 pm ET / 9 am PT / 4 pm GMT. Each session will run for about three hours.{' '}
                      <br className="d-none d-xl-block" />
                      <i>Join live if you can! One-day replays will be available.</i>
                    </p>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL id="pbccw-video" color="light-gray" angle="both">
          <Container>
            <Row className="justify-content-center">
              <Col lg="10" className="video-col px-0 px-sm-3">
                {landingVideo}
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL
          id="pbccw-movement"
          color="white"
          imgPosition="right"
          imgUrl="https://cdn.foodrevolution.org/pbcc/images/1494492867-square.jpg"
          angleBottomDisplay="lg-none"
        >
          <Container>
            <Row>
              <Col>
                <p>
                  Around the world, doctors, hospitals, companies, health insurers, and governments are promoting
                  healthier diets and lifestyle patterns. Nearly ¾ of all Americans now want to eat healthier food, and
                  tens of millions of people are asking, “Is a more plant-based diet right for me?”
                </p>
                <p>
                  But when people seek real change, they need more than headlines and good intentions. They need
                  guidance, encouragement, and support.
                </p>
                <p>
                  That’s where <i>you</i> come in.
                </p>
                <p>
                  As interest in healthy eating grows, there’s increasing demand for people who can help others navigate
                  the journey with clarity, compassion, and credibility.
                </p>
                <p>Effective plant-based coaching takes three things:</p>
                <p>
                  <b>Nutritional education.</b>
                </p>
                <p>
                  <b>Powerful coaching skills.</b>
                </p>
                <p>
                  <b>And the tools and strategies to make an Impact.</b>
                </p>
                <p>And that’s exactly what we’re covering in this upcoming three-part workshop.</p>
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL
          id="pbccw-series"
          color="white"
          imgPosition="left"
          imgUrl="https://cdn.foodrevolution.org/pbcc/images/1477929691-square.jpg"
          innerClass="sectionCL-padding-lg"
        >
          <Container>
            <Row>
              <Col>
                <p>
                  Coaches who master the art of guiding clients through their transformative journeys are poised to make
                  a positive impact on lives, families, and entire communities.
                </p>
                <p>
                  They’ll do meaningful work, whether by growing a purpose-driven coaching business, securing roles in
                  innovative companies, or helping shape a healthier future through leadership and advocacy.
                </p>
                <p>
                  The same principles that empower you to change the world can also be applied to coaching your loved
                  ones… and even yourself.
                </p>
                <p>
                  <b>And it can’t wait.</b>
                </p>
                <p>The modern diet is fueling an epidemic of disease — and killing millions of people.</p>
                <p>It’s costing us trillions of dollars in medical costs.</p>
                <p>
                  And industrialized agriculture has become one of the most environmentally destructive forces on the
                  planet.
                </p>
                <p>We can do better.</p>
                <p>We can save lives, save money, and help to save the planet.</p>
                <p>
                  <b>
                    Never before has there been such a tremendous opportunity to be a beacon of hope and an agent of
                    change.
                  </b>
                </p>
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL id="pbccw-discover" color="light-gray">
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading text-center">In this LIVE workshop, you’ll discover:</h3>
                <div className="d-flex justify-content-center px-4">
                  {
                    {
                      default: (
                        <ListIcon className="m-0">
                          <>
                            The simple dietary shifts that can kick off a healing transformation and the specific
                            nutrients that are shown to increase lifespan, improve heart health, starve cancer, slash
                            the risk of dementia, decrease type 2 diabetes markers, and much more
                          </>
                          <>
                            Advanced coaching strategies for supporting health through whole food, plant-based living —
                            including how to make complex science simple, relevant, and accessible.
                          </>
                          <>
                            The big mistake that outdated coaching techniques make and how to motivate your clients to
                            go from <i>wanting</i> to change… to <i>doing</i> it.
                          </>
                          <>
                            How graduates of our plant-based coaching certificate have transformed their passions into
                            meaningful careers. They’ll share openly and candidly about the full experience.
                          </>
                          <>
                            How to clarify and articulate your core purpose, so you can lead with confidence and attract
                            the clients, opportunities, and impact that align with your deepest values.
                          </>
                          <>
                            How to unleash your potential by exploring dynamic coaching pathways — whether you start
                            your own thriving business, find fulfilling employment, support friends and loved ones, or
                            all three!
                          </>
                          <>
                            The perils and potentials of AI — and how you can use AI tools to massively accelerate your
                            impact, with integrity.
                          </>
                        </ListIcon>
                      ),
                      ad: (
                        <ListIcon className="m-0">
                          <>
                            What the latest research says about which foods have the power to combat disease markers.
                            Learn the simple dietary shifts that can kick off a healing transformation in yourself and
                            your clients, and the specific nutrients that are linked to a long lifespan
                          </>
                          <>
                            Advanced coaching strategies for supporting health through whole food, plant-based living —
                            including how to make complex science simple, relevant, and accessible.
                          </>
                          <>
                            Food Revolution Network’s essential coaching skills and unique techniques, and see how our
                            alumni have applied them in their own growing careers
                          </>
                          <>
                            The big mistake that outdated coaching techniques make — and how to motivate clients to go
                            from <i>wanting</i> to change… to <i>doing</i> it.
                          </>
                          <>
                            How our plant-based coaching graduates have transformed their passions into meaningful
                            careers. They’ll share openly and candidly about the full experience.
                          </>
                          <>
                            How to clarify and articulate your core purpose. Learn how to lead with confidence and
                            attract the clients, opportunities, and impact that align with your deepest values.
                          </>
                          <>
                            The perils and potentials of AI — and how you can use AI tools to massively accelerate your
                            impact, with integrity.
                          </>
                          <>
                            Guidance in how to build your new or existing business (and make it into what you wish it to
                            be) and attract and retain clients.
                          </>
                        </ListIcon>
                      )
                    }[variant]
                  }
                </div>
                <p className="text-center mt-5 mb-0">
                  <OptinModalTrigger>Save My Spot</OptinModalTrigger>
                </p>
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL id="pbccw-bonus" color="white">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading text-center">Join Us and Receive This Guidebook (for free):</h2>
                <Row className="align-items-center">
                  <Col xs="12" lg="4" className="text-center">
                    <img
                      className="mb-4 w-auto"
                      src="https://cdn.foodrevolution.org/pbcc/images/pbcc-using_ai_to_enhance_productivity-cover.png"
                      alt="Using AI to Enhance Productivity: A Plant-Based Coaching Certificate Guide cover image"
                      width={1500}
                      height={1500}
                      style={{maxHeight: '500px'}}
                    />
                  </Col>
                  <Col xs="12" lg="8" className="text-center text-lg-left">
                    <h4 className="mt-0">
                      <i>Using AI to Enhance Productivity: A Plant-Based Coaching Certificate Guide</i>
                    </h4>
                    <p>
                      This comprehensive guidebook details and illuminates the many helpful ways in which specific
                      artificial intelligence tools can assist you as you build a career in plant-based nutrition and
                      coaching (or any other endeavor!). We highlight some of the top relevant AI programs and how each
                      can serve your content creation, productivity, organization, marketing, and even communications.
                      You’ll find out how you can save time, gain insights, and spend more time doing what you love. You
                      can use AI to generate the perfect image or positioning to accompany your websites, newsletters,
                      and marketing. It can make a video for you, help you draft a guidebook for your clients, or even
                      help you with graphic design and translation!
                    </p>
                  </Col>
                </Row>
                <div className="text-center">
                  <p>Save your spot, show up live, and get this AI guidebook bonus for FREE.</p>
                  <p className="mt-4 mb-0">
                    <OptinModalTrigger>Save My Spot</OptinModalTrigger>
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL
          id="pbccw-ocean"
          color="black"
          imgUrl="https://cdn.foodrevolution.org/ppt/images/175234125.jpg"
          angle="none"
        >
          <Container>
            <Row className="justify-content-center">
              <Col lg="11">
                <div className="background-white rounded box-shadow-black-50 p-4 p-lg-5">
                  <Row className="align-items-center mb-4">
                    <Col xs="12" md="4" lg="2" className="mb-4 mb-md-0">
                      <img
                        className="rounded-circle"
                        src="https://cdn.foodrevolution.org/global/team/frn-guest_expert-portrait-square-ocean_robbins.jpg"
                      />
                    </Col>
                    <Col xs="12" md="8" lg="10">
                      <h3 className="section-only-heading">Hi, I’m Ocean Robbins</h3>
                    </Col>
                  </Row>
                  <p>Interest in healthy eating is on the rise — and that’s great news.</p>
                  <p>But let’s be honest: a lot of people are also confused.</p>
                  <p>
                    While some are turning to plant-based eating, many are getting swept up in trendy diets — keto,
                    carnivore, low-carb, low-lectin — that may offer short-term benefits but often lack solid science
                    and long-term sustainability. Too often, they leave people feeling worse, not better.
                  </p>
                  <p>
                    At the same time, more folks than ever are looking for a way to eat that’s nourishing, lasting, and
                    aligned with their values. They want to feel better, prevent disease, and make a difference — but
                    they don’t always know how to start, or who to trust.
                  </p>
                  <p>That’s where you come in.</p>
                  <p>
                    There’s a critical need right now for leaders who can cut through the noise and offer clear,
                    evidence-based, plant-powered guidance — for health, for compassion, and for the future of our
                    planet.
                  </p>
                  <p>
                    If you join me and our expert guests for this workshop, we’ll show you how plant-based coaching can
                    help you turn your passion into meaningful impact. (You’ll also meet several of our alumni who have
                    built successful careers at all levels.) You’ll learn how to support others in making real, lasting
                    changes — and how to be part of a movement that’s grounded in truth, empowered by science, and
                    fueled by love.
                  </p>
                  <p>See you there,</p>
                  <img
                    className="w-auto"
                    src="https://cdn.foodrevolution.org/global/team/ocean-signature-firstname.png"
                    alt="ocean signature"
                    width={1000}
                    height={249}
                    style={{maxHeight: '60px'}}
                  />
                </div>
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL id="pbccw-facilitators" color="white" angle="both">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading text-center">Your Workshop Facilitators</h2>
                <Row className="align-items-stretch justify-content-center text-center">
                  {workshopSpeakerList.map((item, i) => {
                    return (
                      <Fragment key={item + i}>
                        {speakers
                          .filter((speaker) => speaker.id === item.id)
                          .map((speaker) => {
                            const [isOpen, setIsOpen] = useState(false);
                            const onClick = useCallback(() => setIsOpen(true), []);
                            const toggle = useCallback(() => setIsOpen((isOpen) => !isOpen), []); //
                            return (
                              <Col key={'speaker' + i} xs="6" lg="3" className="mb-4 d-flex flex-column">
                                <img className="box-shadow-image rounded mb-3" src={speaker.image} />
                                <h6 className="text-base mt-0 mb-2">{speaker.name}</h6>
                                <p className="balance-text text-16 mb-3">
                                  {speaker.taglineVariant ? speaker.taglineVariant : speaker.tagline}
                                </p>
                                {speaker.bio && (
                                  <div className="mt-auto">
                                    <Button color="green" onClick={onClick} className="text-small text-white" block>
                                      Read Bio
                                    </Button>
                                    <Modal isOpen={isOpen} toggle={toggle} size="lg">
                                      <Container className="px-sm-4 pt-0 pb-5" fluid>
                                        <Row className="d-flex justify-content-center">
                                          <Col xs="10" lg="4" className="mb-4 mb-lg-0">
                                            <LazyLoad height={300} offset={100}>
                                              <img className="box-shadow-image rounded" src={speaker.image} />
                                            </LazyLoad>
                                          </Col>
                                          <Col xs="12" lg="8">
                                            <div className="speaker-details">
                                              <h3 className="speaker-name section-heading mb-2">{speaker.name}</h3>
                                              <ReactMarkdown className="speaker-description" source={speaker.bio} />
                                            </div>
                                          </Col>
                                        </Row>
                                      </Container>
                                    </Modal>
                                  </div>
                                )}
                              </Col>
                            );
                          })}
                      </Fragment>
                    );
                  })}
                </Row>
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL id="" color="light-gray" className="text-center">
          <Container>
            <Row>
              <Col>
                <img
                  className="mb-4"
                  src="https://cdn.foodrevolution.org/global/frn-logo-2026-trademarked-color.svg"
                  alt="Food Revolution Network logo"
                  width={300}
                  height={126}
                  style={{maxHeight: '200px'}}
                />
                <p className="balance-text">
                  Food Revolution Network educates and advocates for healthy, ethical, and sustainable food for all.
                </p>
                <p className="balance-text">
                  With a community of over a million and working alongside leading voices in food and health, we deliver
                  credible, research-based guidance to transform how the world eats.
                </p>
                <p className="balance-text">
                  As a certified B Corp, we commit to these values in every aspect of our work.
                </p>
                <img
                  src="https://cdn.foodrevolution.org/global/b-corp-logo-black.svg"
                  alt="B Corp Logo"
                  width={103}
                  height={150}
                  style={{maxHeight: '200px'}}
                />
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL id="pbccw-info" color="white">
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading text-center mb-5">Join us LIVE November 18 – 20, 2025!</h3>
                <ImageCardCL
                  image="https://cdn.foodrevolution.org/pbcc/images/1838520701-6x5.jpg"
                  imageAlt="woman sits across from unrecognizable woman in chairs next to window"
                  className="cardDay1"
                >
                  <h6 className="section-heading text-center mb-2">Day 1</h6>
                  <h6 className="text-center mt-0 mb-3">Purpose-Driven Coaching: Real Stories, Real Impact</h6>
                  <ul className="text-16">
                    <li>
                      <b>Discover and Define Your Purpose:</b> Gain powerful tools to clarify and articulate your “why,”
                      so you can align your coaching journey with your deepest values.
                    </li>
                    <li>
                      <b>Learn Advanced Nutritional Strategies for Longevity and Vitality:</b> Explore evidence-based
                      nutrition insights with a renowned dietitian to deepen your knowledge and expand your ability to
                      help others thrive.
                    </li>
                    <li>
                      <b>Unlock the Little-Known Art of Transformative Coaching:</b> Dive into six proven techniques for
                      guiding behavior change that lasts, so you can help people move from inspiration to real results.
                    </li>
                    <li>
                      <b>Engage with Experts and Alumni:</b> Ask questions, connect with our speakers, and hear stories
                      from graduates who are already making an impact in the world.
                    </li>
                    <li>
                      <b>Integrate and Clarify Your Next Steps:</b> Walk away with renewed clarity, practical tools for
                      change, and a deeper connection to the “why” that fuels your coaching journey.
                    </li>
                  </ul>
                </ImageCardCL>
                <ImageCardCL
                  image="https://cdn.foodrevolution.org/pbcc/images/1740809156-6x5.jpg"
                  imageAlt="waist-up view of senior man holding kitchen knife and carrot, mature woman holding smart phone, both in aprons and laughing."
                  className="cardDay2"
                >
                  <h6 className="section-heading text-center mb-2">Day 2</h6>
                  <h6 className="text-center mt-0 mb-3">The Inner Work: Nutrition and Self-Care in Coaching</h6>
                  <ul className="text-16">
                    <li>
                      <b>Strengthen Your Mindset for Impact:</b> Discover the tools and practices that help you turn
                      inspiration into sustainable action — for yourself and the people you’ll coach.
                    </li>
                    <li>
                      <b>Cut Through Confusion with Clarity:</b> Learn how to debunk common nutrition myths with
                      authority, so you can build trust and confidence with future clients.
                    </li>
                    <li>
                      <b>Communicate with Power and Heart:</b> Develop the knack for making plant-based messages clear,
                      actionable, and inspiring, whether you’re speaking to individuals, groups, or entire communities.
                    </li>
                    <li>
                      <b>Learn from Real-World Examples:</b> Hear from alumni who are already putting these skills into
                      practice, and pick up practical tips and insights from their journeys.
                    </li>
                    <li>
                      <b>Bridge Knowledge into Transformation:</b> Leave Day 2 with a deeper connection to the inner
                      work of coaching, and the ability to translate nutrition knowledge into true, lasting change for
                      both you and those you serve.
                    </li>
                  </ul>
                </ImageCardCL>
                <ImageCardCL
                  image="https://cdn.foodrevolution.org/pbcc/images/2220160484-6x5.jpg"
                  imageAlt="portrait of a businesswoman holding a clipboard in a modern office with a green world map in the background"
                  className="cardDay3 mb-5"
                >
                  <h6 className="section-heading text-center mb-2">Day 3</h6>
                  <h6 className="text-center mt-0 mb-3">
                    Success Your Way: Growing Your Coaching Practice with Integrity in a Changing World
                  </h6>
                  <ul className="text-16">
                    <li>
                      <b>Build with Purpose and Integrity:</b> Discover how to grow a coaching practice that’s grounded
                      in your values, aligned with your vision, and resilient in a rapidly changing world.
                    </li>
                    <li>
                      <b>Harness AI Wisely:</b> Learn how to use AI ethically and effectively — as a supportive tool
                      that enhances your work while keeping the heart of coaching human-centered and
                      relationship-driven.
                    </li>
                    <li>
                      <b>Amplify Impact Through Groups:</b> Explore how group coaching can expand your influence,
                      increase access, and create powerful peer-to-peer accountability and transformation.
                    </li>
                    <li>
                      <b>Celebrate Diverse Paths to Success:</b> Hear directly from our alumni in a dynamic panel
                      discussion, as they share how success in coaching isn’t about fitting a mold, it’s about aligning
                      your work with your values and making a difference where it matters most to you.
                    </li>
                    <li>
                      <b>Design Aligned Coaching Packages:</b> Gain practical guidance on creating coaching programs and
                      packages that reflect your values and allow you to serve with clarity, compassion, and grace.
                    </li>
                    <li>
                      <b>Step Forward with Confidence:</b> Leave Day 3 with renewed clarity, practical resources, and
                      the confidence to launch or grow a coaching practice that is uniquely yours, and that makes a
                      meaningful difference.
                    </li>
                  </ul>
                </ImageCardCL>
                <p>
                  We’ll also introduce Food Revolution Network’s Plant-Based Coaching Certificate, where you can gain
                  the skills to:
                </p>
                <ListIcon>
                  <>
                    Launch a successful business as a Plant-Based Coach, or add plant-based coaching to your existing
                    practice.
                  </>
                  <>Get hired as a part-time or full-time employee by forward-thinking companies and organizations.</>
                  <>Be part of the plant-based coaching revolution.</>
                </ListIcon>
                <p>
                  <i>*With a special offer that’s only available for workshop participants!</i>
                </p>
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL
          id="pbccw-cta-final"
          color="purple"
          imgPosition="left"
          imgUrl="https://cdn.foodrevolution.org/pbcc/images/1832547402-square.jpg"
          className="text-center text-white"
          angle="none"
          order="last"
        >
          <h2 className="section-heading">It’s Time for a Healthy Food Revolution.</h2>
          <h3 className="mt-0 mb-5">
            <i>Let’s do this, together.</i>
          </h3>
          <OptinModalTrigger>{ctaButtonCopy}</OptinModalTrigger>
        </SectionCL>
      </div>

      {!noExitIntent && (
        <ExitModal
          exitDelay={30000}
          content={
            <>
              <h4 className="section-heading text-400 mb-4">
                You can turn your passion for healthy eating into <b>purpose</b> (and an income!).
              </h4>
              <p className="mb-0">
                Discover how plant-based coaching can unlock real and lasting change — for yourself, your loved ones,
                your clients, and your community.
              </p>
            </>
          }
          image={{
            url: 'https://cdn.foodrevolution.org/pbcc/images/1495153047-exit.jpg',
            alt: '',
            width: 1200,
            height: 960
          }}
          positiveOptin
          positiveButtonText="Register Now"
          positiveButtonColor="exit-orange"
          negativeButtonText="No Thanks"
        />
      )}

      <div className="footer-content mt-auto">
        <Footer />
      </div>

      <OptinModal
        contactFormHeader={
          <>
            <p className="text-center">
              <b>Enter your name and email below to sign up.</b>
            </p>
          </>
        }
        btnText="Save Your Spot"
      />
    </div>
  );
}
